import JSZip from 'jszip';
import { IMPORT_STUDY_MESSAGES } from './importStudyMessages';
import { isLikelyDicomDatasetArrayBuffer } from './isDicomArrayBuffer';

export type ZipExtractionResult =
  | { success: true; dicomFiles: File[] }
  | { success: false; message: string };

function getEntryBaseName(entryPath: string): string {
  const segments = entryPath.split('/').filter(Boolean);
  return segments[segments.length - 1] || entryPath;
}

function getFileExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex <= 0) {
    return '';
  }

  return fileName.slice(dotIndex).toLowerCase();
}

function hasDicomFileExtension(fileName: string): boolean {
  const extension = getFileExtension(fileName);
  return extension === '.dcm' || extension === '.dicom';
}

function shouldIncludeZipEntry(fileName: string, arrayBuffer: ArrayBuffer): boolean {
  if (isLikelyDicomDatasetArrayBuffer(arrayBuffer)) {
    return true;
  }

  return hasDicomFileExtension(fileName);
}

/**
 * Extracts valid DICOM files from a ZIP archive in the browser.
 * Folder entries are ignored. Files are not persisted.
 */
export async function extractDicomFilesFromZip(zipFile: File): Promise<ZipExtractionResult> {
  let zip: JSZip;

  try {
    zip = await JSZip.loadAsync(zipFile);
  } catch {
    return { success: false, message: IMPORT_STUDY_MESSAGES.ZIP_READ_FAILED };
  }

  const dicomFiles: File[] = [];
  const fileEntries = Object.values(zip.files).filter(entry => !entry.dir);

  for (const entry of fileEntries) {
    const arrayBuffer = await entry.async('arraybuffer');
    const fileName = getEntryBaseName(entry.name);

    if (!shouldIncludeZipEntry(fileName, arrayBuffer)) {
      continue;
    }

    dicomFiles.push(
      new File([arrayBuffer], fileName, {
        type: 'application/dicom',
        lastModified: zipFile.lastModified,
      })
    );
  }

  if (!dicomFiles.length) {
    return { success: false, message: IMPORT_STUDY_MESSAGES.EMPTY_ZIP };
  }

  return { success: true, dicomFiles };
}
